import { useState, useCallback, useRef, useEffect } from 'react';
import ConfirmModal from '../../components/shared/ConfirmModal';

/**
 * Confirmation impérative — remplace `window.confirm` sans réécrire les flux.
 *
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   const ok = await confirm({ title, message, confirmLabel, cancelLabel, variant });
 *   if (ok) { ... }
 *
 * `ConfirmDialog` est un ÉLÉMENT (ou null) à monter une fois dans le JSX :
 *   return (<> ... {ConfirmDialog} </>);
 *
 * `variant` : 'warning' (orange, défaut) | 'danger' (rouge, destructif).
 * Annulation / clic hors zone / démontage → résout `false` (aucune action fantôme).
 */
export default function useConfirm() {
  const [opts, setOpts] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    setOpts(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setOpts(options);
      }),
    [],
  );

  // Démontage pendant l'attente → on résout false (pas d'action fantôme).
  useEffect(
    () => () => {
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
    },
    [],
  );

  const ConfirmDialog = opts ? (
    <ConfirmModal
      title={opts.title}
      message={opts.message}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      variant={opts.variant}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { confirm, ConfirmDialog };
}
